// Task.types.ts
import {
  CommonStatus,
  type AuditFields,
  type CommonFields,
  type GenericID,
  type PartnerRef,
  type UserRef,
} from "./common";
import type { User } from "./user";
import { DateYMDString, ISOString } from "./date";
import { PandaDocReference } from "./external/pandaDoc";
import { formatToTitleCase } from "@/utils/formatters/stringFormatters";

export enum TaskStatus {
  OPEN = CommonStatus.OPEN,
  IN_PROGRESS = CommonStatus.IN_PROGRESS,
  COMPLETED = CommonStatus.COMPLETED,
  CANCELLED = CommonStatus.CANCELLED,
}

export const TASK_STATUS_CHOICES: { id: TaskStatus; name: string }[] = [
  { id: TaskStatus.OPEN, name: formatToTitleCase(TaskStatus.OPEN) },
  { id: TaskStatus.IN_PROGRESS, name: formatToTitleCase(TaskStatus.IN_PROGRESS) },
  { id: TaskStatus.COMPLETED, name: formatToTitleCase(TaskStatus.COMPLETED) },
  { id: TaskStatus.CANCELLED, name: formatToTitleCase(TaskStatus.CANCELLED) },
];

// Lookup keyed by the canonical value with separators/casing stripped, so any
// incoming variant ("OPEN", "In Progress", "in-progress") maps to one enum member.
// Built from TASK_STATUS_CHOICES (the single canonical list) rather than
// Object.values(TaskStatus): because the enum members are initialised from
// CommonStatus references, TS emits reverse mappings, so Object.values would also
// include the UPPERCASE key names.
const TASK_STATUS_BY_KEY: Record<string, TaskStatus> = TASK_STATUS_CHOICES.reduce(
  (acc, { id }) => {
    acc[id.toLowerCase().replace(/[\s-]+/g, "_")] = id;
    return acc;
  },
  {} as Record<string, TaskStatus>
);

/**
 * Coerce a raw status value to the canonical lowercase `TaskStatus` enum.
 *
 * Task status can arrive in more than one casing depending on the source, which
 * broke option-selection matching and produced the duplicated/UPPERCASE picker
 * entries in ADO #5164/#5165. We normalise once at ingress (the data provider)
 * so every picker and comparison keys off a single canonical enum (#6310 AC3),
 * rather than de-duplicating option lists downstream. Unknown values pass through
 * unchanged so an unexpected status never crashes the client. Absent/blank input
 * (null, undefined, "", or whitespace-only) coerces to `undefined` — a task with
 * no status reads as "no status", not as an empty string.
 */
export const normalizeTaskStatus = (raw?: string | null): TaskStatus | undefined => {
  if (raw === undefined || raw === null) return undefined;
  const key = String(raw)
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  if (!key) return undefined; // "" and whitespace-only both collapse here
  return TASK_STATUS_BY_KEY[key] ?? (raw as TaskStatus);
};

/**
 * Object enum that can be referenced by tasks - matches backend common.ObjectType
 */

export enum ObjectType {
  STOPLOSS_CLAIMS = "stoploss-claims",
  REIMBURSEMENTS = "reimbursement",
  CLAIMANTS = "claimants",
  POLICIES = "policies",
  CLIENTS = "clients",
  ENDORSEMENTS = "endorsements",
  DOCUMENTS = "documents",
}

/**
 * Reference to another object (record) in the system
 */
export interface TaskRecordReference {
  object_type?: ObjectType;
  object_id?: string | null;
  label?: string;
}

/**
 * Base task comment interface - fields that are in both create and read
 */
interface TaskCommentBase {
  content?: string;
  mentions?: UserRef[];
}

/**
 * Full task comment record from the API - includes server-managed fields and nested comments
 */
export interface TaskComment extends TaskCommentBase, GenericID, UserRef {
  created_by?: UserRef;
  created_datetime?: ISOString;
  modified_by?: UserRef;
  modified_datetime?: ISOString;
  is_edited?: boolean;
  comments?: TaskComment[];
  user?: User;
}

/**
 * Payload for creating a new comment
 */
export interface TaskCommentCreate {
  content: string;
  user_id_ref?: string; // Optional - backend can infer from auth
  mentions?: UserRef[];
}

/**
 * Base task interface - fields that are in both create and read
 */
interface TaskBase {
  title?: string;
  description?: string;
  status?: TaskStatus;
  completed?: boolean;
  due_date?: DateYMDString;
  display_claim_exhibit?: boolean;
  record?: TaskRecordReference;
  assigned_user_id_ref?: string;
  assigned_role_id_ref?: string;
  template_id_ref?: string;
  comments?: TaskComment[];
}

/**
 * Full task record from the API - includes server-managed fields
 */
export interface Task extends TaskBase, CommonFields, AuditFields, PartnerRef {
  /** Producer-specific context, e.g. a deductible review's id and policy (#4243). */
  metadata?: Record<string, unknown>;
  policy_id_ref?: string;
  // Expandable fields
  pandadoc?: PandaDocReference;
  // Server-computed signature eligibility
  is_signature_eligible?: boolean;
  // Server-derived partner standing CC — display-only (injected server-side at send time)
  signature_standing_cc?: string;
}

/**
 * Payload for creating a new task
 * Required fields: title, status (per backend validation)
 */
export interface TaskCreate extends TaskBase, PartnerRef {
  title: string;
  status: TaskStatus;
}

/**
 * Payload for updating a task - all fields optional except id
 */
export interface TaskUpdate extends Partial<TaskBase>, Partial<PartnerRef> {
  id: string;
}

/**
 * Filter object for querying task templates
 */
export interface TaskTemplateFilter {
  is_active: boolean;
  record_subject?: string;
  applicable_object_types?: string;
  trigger_event?: string;
  title?: string;
}

// ==================== TASK TEMPLATE TYPES ====================

/**
 * Grule-based business logic rules stored as JSONB on the template.
 */
export interface BusinessLogicRules {
  rule_text: string;
  required_facts?: string[];
  description?: string;
}

/**
 * Alert / notification settings stored as JSONB on the template.
 */
export interface AlertNotificationSettings {
  notify_at_creation?: boolean;
  notify_record_owner?: boolean;
  custom_recipients?: string[];
}

/**
 * Usage analytics returned by GET /task-templates/{id}/usage-stats.
 */
export interface TemplateUsageStats {
  template_id: string;
  tasks_created: number;
  tasks_completed: number;
  tasks_open: number;
  tasks_in_progress: number;
  success_rate: number;
}

/**
 * Response from POST /task-templates/validate-rule.
 */
export interface RuleValidationResult {
  valid: boolean;
  error?: string;
  status?: string;
}

/**
 * Full task template record from the API.
 */
export interface TaskTemplate {
  id: string;
  title: string;
  description?: string;
  record_subject?: string;
  trigger_event?: string;
  template_category?: string;
  priority?: number;
  rough_order?: number;
  applicable_object_types?: ObjectType | ObjectType[];
  default_due_days?: number;
  default_show_on_claim_exhibit?: boolean;
  auto_assign_role_id_ref?: string;
  metadata?: Record<string, string>;
  is_active?: boolean;
  business_logic?: BusinessLogicRules;
  alert_notification?: AlertNotificationSettings;
  event_status_filters?: string[];
  partner_id?: string;
  created_by?: string;
  created_datetime?: string;
  modified_by?: string;
  modified_datetime?: string;
}

/**
 * Payload for creating a new task template.
 */
export interface TaskTemplateCreate {
  title: string;
  description?: string;
  record_subject?: string;
  trigger_event?: string;
  template_category?: string;
  priority?: number;
  default_due_days?: number;
  default_show_on_claim_exhibit?: boolean;
  applicable_object_types?: ObjectType[];
  business_logic?: BusinessLogicRules;
  alert_notification?: AlertNotificationSettings;
  metadata?: Record<string, string>;
}

/**
 * Payload for updating a task template (partial).
 */
export interface TaskTemplateUpdate extends Partial<TaskTemplateCreate> {
  id: string;
}
